import wootConstants from 'dashboard/constants/globals';

// A conversation's status is otherwise invisible in the conversation list. These
// drive a coloured edge down the row so the state reads at a glance:
//   open     - with a human
//   pending  - waiting on the bot, not with a human yet
//   resolved - done
//   snoozed  - parked until its wake-up time
// Open and snoozed deliberately share the same muted grey: both are states that
// need no immediate attention. The alarm icon is what separates them, so snoozed
// is the only status that carries one.
//
// The class names must stay literal in a file Tailwind scans (it collects classes
// by reading source), which `dashboard/helper/**/*.js` is.
const CONVERSATION_STATUS_INDICATORS = {
  [wootConstants.STATUS_TYPE.OPEN]: {
    edgeClass: 'bg-n-slate-9',
    icon: '',
    iconClass: '',
  },
  [wootConstants.STATUS_TYPE.PENDING]: {
    edgeClass: 'bg-n-amber-9',
    icon: '',
    iconClass: '',
  },
  [wootConstants.STATUS_TYPE.RESOLVED]: {
    edgeClass: 'bg-n-teal-9',
    icon: '',
    iconClass: '',
  },
  [wootConstants.STATUS_TYPE.SNOOZED]: {
    edgeClass: 'bg-n-slate-9',
    icon: 'i-ph-alarm',
    iconClass: 'text-n-slate-11',
  },
};

/**
 * Visual treatment for a conversation status in the conversation list.
 * @param {String} status - Conversation status, e.g. 'pending'.
 * @returns {{edgeClass: String, icon: String, iconClass: String}|null} Indicator,
 *   or null for a status we do not mark.
 */
export const getConversationStatusIndicator = status =>
  CONVERSATION_STATUS_INDICATORS[status] || null;

/**
 * Determines the last non-activity message between store and API messages.
 * @param {Object} messageInStore - The last non-activity message from the store.
 * @param {Object} messageFromAPI - The last non-activity message from the API.
 * @returns {Object} The latest non-activity message.
 */
const getLastNonActivityMessage = (messageInStore, messageFromAPI) => {
  // If both API value and store value for last non activity message
  // are available, then return the latest one.
  if (messageInStore && messageFromAPI) {
    return messageInStore.created_at >= messageFromAPI.created_at
      ? messageInStore
      : messageFromAPI;
  }
  // Otherwise, return whichever is available
  return messageInStore || messageFromAPI;
};

/**
 * Filters out duplicate source messages from an array of messages.
 * @param {Array} messages - The array of messages to filter.
 * @returns {Array} An array of messages without duplicates.
 */
export const filterDuplicateSourceMessages = (messages = []) => {
  const messagesWithoutDuplicates = [];
  // We cannot use Map or any short hand method as it returns the last message with the duplicate ID
  // We should return the message with smaller id when there is a duplicate
  messages.forEach(m1 => {
    if (m1.source_id) {
      const index = messagesWithoutDuplicates.findIndex(
        m2 => m1.source_id === m2.source_id
      );

      if (index < 0) {
        messagesWithoutDuplicates.push(m1);
      }
    } else {
      messagesWithoutDuplicates.push(m1);
    }
  });
  return messagesWithoutDuplicates;
};

/**
 * Retrieves the last message from a conversation, prioritizing non-activity messages.
 * @param {Object} m - The conversation object containing messages.
 * @returns {Object} The last message of the conversation.
 */
export const getLastMessage = m => {
  const lastMessageIncludingActivity = m.messages[m.messages.length - 1];

  const nonActivityMessages = m.messages.filter(
    message => message.message_type !== 2
  );
  const lastNonActivityMessageInStore =
    nonActivityMessages[nonActivityMessages.length - 1];

  const lastNonActivityMessageFromAPI = m.last_non_activity_message;

  // If API value and store value for last non activity message
  // is empty, then return the last activity message
  if (!lastNonActivityMessageInStore && !lastNonActivityMessageFromAPI) {
    return lastMessageIncludingActivity;
  }

  return getLastNonActivityMessage(
    lastNonActivityMessageInStore,
    lastNonActivityMessageFromAPI
  );
};

/**
 * Filters messages that have been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of read messages.
 */
export const getReadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 <= agentLastSeenAt * 1000
  );
};

/**
 * Filters messages that have not been read by the agent.
 * @param {Array} messages - The array of messages to filter.
 * @param {number} agentLastSeenAt - The timestamp of when the agent last saw the messages.
 * @returns {Array} An array of unread messages.
 */
export const getUnreadMessages = (messages, agentLastSeenAt) => {
  return messages.filter(
    message => message.created_at * 1000 > agentLastSeenAt * 1000
  );
};
